import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { ADVISORY_KEYS, MAX_COMPOSITE_JUDGES, otherJudgeIdsOf } from "../lib/compositeJudgeMap";

// Card #1742 / #3417 -- create/edit one composite-judge mapping. The PRIMARY
// judge and the OTHER judges are picked explicitly from the show's roster
// (separate Selects); never inferred from array order or parsed out of the
// composite judge's name (#1342's background is that "Williams Koch" is FVD
// packing several real judges into one roster row, not a name to split).
// A composite is a primary plus 1 to 4 others (2 to 5 judges in all, USEF
// DR126.2.e.8 caps a young horse jury at 3, which is only an advisory here).
// The others are unranked and kept in the order they were added.
//
// Backend codes (apps/score-api/src/handlers/composite-judge-map.js) are
// mapped to curated, static i18n keys here rather than shown raw -- same
// pattern as RideEditor's RESEND_ERROR_KEYS -- since common-fe's
// installServerErrorInterceptor rewrites err.message to a generic string and
// stashes the real code on err.response.data.code.
const ERROR_KEYS = {
  primary_judge_not_found: "judges.compositeTab.errorPrimaryNotFound",
  other_judge_not_found: "judges.compositeTab.errorOtherNotFound",
  composite_judge_count_invalid: "judges.compositeTab.errorCountInvalid",
  composite_judge_not_found: "judges.compositeTab.errorCompositeNotFound",
  duplicate_judge: "judges.compositeTab.errorDuplicateJudge",
  self_mapping: "judges.compositeTab.errorSelfMapping",
  already_mapped: "judges.compositeTab.errorAlreadyMapped",
};

export default function CompositeJudgeMapDialog({
  open,
  onClose,
  activeShowId,
  compositeJudge,
  existingMapping,
  judgeOptions,
  onSaved,
}) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();

  const [primaryJudgeId, setPrimaryJudgeId] = useState("");
  // One entry per "Other judge" row, in the order added. "" = row not picked yet.
  const [otherJudgeIds, setOtherJudgeIds] = useState([""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // GET /composite-judge-map/:id facts about the SAVED mapping (edit only):
  // how many sheets are already completed and the advisories.
  const [usage, setUsage] = useState(null);

  // This Dialog is already bumped above the AppBar (modal + 100, below); each
  // Select's own popover Menu defaults to zIndex.modal and would otherwise
  // render BEHIND the Dialog's container — same fix BulkAssignScribesDialog /
  // ClassDetailDrawer use for their pickers (menuProps at modal + 200, one
  // layer above this Dialog).
  const menuProps = { sx: { zIndex: (theme) => theme.zIndex.modal + 200 } };

  // Re-seed from the existing mapping (edit) or blank (create: a primary row
  // plus one other row) every time the dialog is opened for a (possibly
  // different) composite judge.
  useEffect(() => {
    if (!open) return;
    setPrimaryJudgeId(existingMapping?.primaryJudgeId || "");
    const others = otherJudgeIdsOf(existingMapping);
    setOtherJudgeIds(others.length > 0 ? others : [""]);
    setError(null);
  }, [open, existingMapping]);

  // Editing: ask the API how many sheets are already completed under this
  // composite and for its advisories (the list response carries neither). A
  // failure only hides the warnings, it never blocks the edit.
  const editingId = open && existingMapping && compositeJudge ? compositeJudge.id : null;
  useEffect(() => {
    setUsage(null);
    if (!editingId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const got = unwrap(await scoreApi.judges.getCompositeJudgeMap(editingId));
        if (!cancelled) setUsage(got);
      } catch {
        /* warnings are advisory, the edit still works without them */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, scoreApi]);

  const everyone = [primaryJudgeId, ...otherJudgeIds];
  const canSave =
    !!primaryJudgeId &&
    otherJudgeIds.length >= 1 &&
    otherJudgeIds.every(Boolean) &&
    new Set(everyone).size === everyone.length &&
    !saving;

  const setOtherAt = (index, value) =>
    setOtherJudgeIds((prev) => prev.map((id, i) => (i === index ? value : id)));
  const removeOtherAt = (index) =>
    setOtherJudgeIds((prev) => prev.filter((_, i) => i !== index));
  const addJudge = () => setOtherJudgeIds((prev) => [...prev, ""]);
  const atLimit = everyone.length >= MAX_COMPOSITE_JUDGES;

  // Non-blocking warnings. An advisory is only shown while the picker still
  // holds more judges than that advisory's cap, so trimming the list clears it.
  const completedSheetCount = usage?.completedSheetCount || 0;
  const advisories = (usage?.advisories || []).filter(
    (a) => ADVISORY_KEYS[a.code] && everyone.length > (a.maxJudges ?? 0),
  );

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!compositeJudge || !canSave) return;
    try {
      setSaving(true);
      setError(null);
      const saved = existingMapping
        ? unwrap(
            await scoreApi.judges.updateCompositeJudgeMap(compositeJudge.id, {
              primaryJudgeId,
              otherJudgeIds,
            }),
          )
        : unwrap(
            await scoreApi.judges.createCompositeJudgeMap({
              showId: activeShowId,
              compositeJudgeId: compositeJudge.id,
              primaryJudgeId,
              otherJudgeIds,
            }),
          );
      onSaved?.(saved);
    } catch (err) {
      const code = err?.response?.data?.code;
      setError({ message: t(ERROR_KEYS[code] || "judges.compositeTab.saveError") });
    } finally {
      setSaving(false);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // AppLayout's AppBar (common-fe) is pinned at zIndex 1300 — same fix as
      // JudgeSignatureModal/RideHistoryDrawer.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" component="span" color="text.primary">
          {compositeJudge
            ? t(
                existingMapping
                  ? "judges.compositeTab.dialogTitleEdit"
                  : "judges.compositeTab.dialogTitleCreate",
                { name: userDisplayName(compositeJudge) },
              )
            : ""}
        </Typography>
        <IconButton onClick={handleClose} aria-label={t("judges.compositeTab.cancel")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="composite-judge-dialog-guidance"
        >
          {t("judges.compositeTab.dialogGuidance")}
        </Typography>

        {completedSheetCount > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="composite-judge-completed-warning">
            {t("judges.compositeTab.completedSheetsWarning", { count: completedSheetCount })}
          </Alert>
        )}
        {advisories.map((a) => (
          <Alert
            key={a.code}
            severity="warning"
            sx={{ mt: 2 }}
            data-testid={`composite-judge-advisory-${a.code}`}
          >
            {t(ADVISORY_KEYS[a.code], { judgeCount: everyone.length, maxJudges: a.maxJudges })}
          </Alert>
        ))}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id="composite-primary-label">
              {t("judges.compositeTab.primaryLabel")}
            </InputLabel>
            <Select
              labelId="composite-primary-label"
              label={t("judges.compositeTab.primaryLabel")}
              value={primaryJudgeId}
              onChange={(e) => setPrimaryJudgeId(e.target.value)}
              MenuProps={menuProps}
            >
              {judgeOptions
                .filter((j) => j.id === primaryJudgeId || !otherJudgeIds.includes(j.id))
                .map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {userDisplayName(j)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {t("judges.compositeTab.primaryHint")}
          </Typography>

          {otherJudgeIds.map((otherId, index) => (
            // Index keys on purpose: rows are unranked and positional, a row
            // has no identity beyond its slot until a judge is picked.
            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControl size="small" fullWidth disabled={saving}>
                <InputLabel id={`composite-other-label-${index}`}>
                  {t("judges.compositeTab.otherLabel")}
                </InputLabel>
                <Select
                  labelId={`composite-other-label-${index}`}
                  label={t("judges.compositeTab.otherLabel")}
                  value={otherId}
                  onChange={(e) => setOtherAt(index, e.target.value)}
                  MenuProps={menuProps}
                  data-testid={`composite-other-judge-${index}`}
                >
                  {judgeOptions
                    .filter(
                      (j) =>
                        j.id === otherId ||
                        (j.id !== primaryJudgeId && !otherJudgeIds.includes(j.id)),
                    )
                    .map((j) => (
                      <MenuItem key={j.id} value={j.id}>
                        {userDisplayName(j)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {index > 0 && (
                <IconButton
                  onClick={() => removeOtherAt(index)}
                  disabled={saving}
                  aria-label={t("judges.compositeTab.removeJudge", { n: index + 1 })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {atLimit
              ? t("judges.compositeTab.addJudgeLimit", { max: MAX_COMPOSITE_JUDGES })
              : t("judges.compositeTab.otherHint")}
          </Typography>

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addJudge}
              disabled={saving || atLimit}
              sx={{ color: colors.foxOrange, "&.Mui-disabled": { color: "text.secondary" } }}
            >
              {t("judges.compositeTab.addJudge")}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: "text.primary" }}>
          {t("judges.compositeTab.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            bgcolor: colors.foxOrange,
            "&:hover": { bgcolor: colors.foxOrange, filter: "brightness(0.92)" },
          }}
        >
          {saving ? t("judges.compositeTab.saving") : t("judges.compositeTab.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
